import { TClip } from '@/features/clips/types';
import { queryClient } from '@/lib/react-query';

type PaginatedClipData = { data: TClip[]; total?: number };
/**
 * Pages come in two shapes under the `['clips', …]` prefix: the flat arrays `useInfiniteClips`
 * produces, and the `{ data, total }` pages the cut lists produce because their header states
 * the server-side total. Both must be patched.
 */
type InfiniteClipPage = TClip[] | PaginatedClipData;
type InfiniteClipData = { pages: InfiniteClipPage[]; pageParams: unknown[] };
type AnyClipCache = TClip | TClip[] | InfiniteClipData | PaginatedClipData | undefined;

const isInfinite = (d: unknown): d is InfiniteClipData =>
  !!d && typeof d === 'object' && Array.isArray((d as InfiniteClipData).pages);

const isPaginated = (d: unknown): d is PaginatedClipData =>
  !!d && typeof d === 'object' && Array.isArray((d as PaginatedClipData).data);

// Updates every React-Query entry that holds the mutated clip so that any consumer
// (`useGetClipById`, `useInfiniteClips`, `useGetBroadcastClips`, …) renders the post-mutation
// state on the next read.  Without this, navigating clip→clip→clip restores the pre-mutation
// snapshot from the listing cache and the just-added TimeKeeping record disappears.
export function patchClipCache(clipId: string, updater: (clip: TClip) => TClip) {
  // 1. Single-clip cache used by useGetClipById on BroadcastScheduleProgramPage.
  queryClient.setQueryData<TClip>([`clips-${clipId}`], prev => (prev ? updater(prev) : prev));

  // 2. Every collection cache under the ['clips', …] prefix (infinite list, paginated by-id).
  //    The matching predicate runs on each cached query's data; only collections that contain
  //    the target clip produce a new reference, so unrelated caches stay stable.
  queryClient.setQueriesData<AnyClipCache>({ queryKey: ['clips'] }, data => {
    if (!data) return data;

    if (isInfinite(data)) {
      let changed = false;
      const pages = data.pages.map(page => {
        const clips = Array.isArray(page) ? page : isPaginated(page) ? page.data : null;
        if (!clips) return page;

        const idx = clips.findIndex(c => c?.id === clipId);
        if (idx === -1) return page;

        changed = true;
        const next = clips.slice();
        next[idx] = updater(clips[idx]);

        return Array.isArray(page) ? next : { ...page, data: next };
      });
      return changed ? { ...data, pages } : data;
    }

    if (isPaginated(data)) {
      const idx = data.data.findIndex(c => c?.id === clipId);
      if (idx === -1) return data;
      const next = data.data.slice();
      next[idx] = updater(data.data[idx]);
      return { ...data, data: next };
    }

    if (Array.isArray(data)) {
      const idx = data.findIndex(c => c?.id === clipId);
      if (idx === -1) return data;
      const next = data.slice();
      next[idx] = updater(data[idx]);
      return next;
    }

    return data;
  });
}
